import { useEffect, useState, useMemo, useCallback, useRef, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import TwoPaneLayout from "@/components/TwoPaneLayout";
import Sidebar from "@/components/Sidebar";
import MajorCombobox from "@/components/MajorCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  SlidersHorizontal,
  MapPin,
  Trophy,
  Building2,
  GraduationCap,
  DollarSign,
  BookOpen,
  TrendingUp,
  Check,
  Plus,
} from "lucide-react";
import { SchoolBrandHeader } from "@/components/school/SchoolBrandHeader";
import { SchoolFitAssessmentPanel } from "@/components/school/SchoolFitAssessmentPanel";
import { SchoolNotesPanel } from "@/components/school/SchoolNotesPanel";
import { SchoolProgramDetailsPanel } from "@/components/school/SchoolProgramDetailsPanel";
import { SchoolSearchCard } from "@/components/school/SchoolSearchCard";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useEndUser } from "@/contexts/EndUserContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSchoolsFromIdsParam } from "@/hooks/useSchoolsFromIdsParam";
import {
  fetchSchools,
  fetchSchoolById,
  fetchCollege,
  type SchoolResult,
  type CollegeDetail,
} from "@/services/schools";
import { fetchPipeline, addToPipeline, removePipelineEntry } from "@/services/pipeline";
import { fetchSchoolNotes, upsertSchoolNote } from "@/services/schoolNotes";
import { fetchMajorsList } from "@/services/profile";
import {
  fetchSchoolGolfFits,
  refreshSchoolGolfFit,
  type GolfFitAssessment,
} from "@/services/schoolFits";
import {
  trackSchoolSearched,
  trackSchoolPipelineToggled,
  trackSchoolsViewed,
} from "@/analytics/analytics";
import { PAGE_SUBTITLE_CLASS, PAGE_TITLE_CLASS } from "@/constants/design";

/* ─── Constants ─── */

const PAGE_SIZE = 20;

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

const DIVISIONS = [
  { value: "D1", label: "NCAA Division I" },
  { value: "D2", label: "NCAA Division II" },
  { value: "D3", label: "NCAA Division III" },
  { value: "NAIA", label: "NAIA" },
];

const CONFERENCES = [
  "ACC",
  "American Athletic",
  "Atlantic 10",
  "Big 12",
  "Big East",
  "Big South",
  "Big Ten",
  "Big West",
  "CAA",
  "Conference USA",
  "Horizon League",
  "Ivy League",
  "MAAC",
  "MAC",
  "Missouri Valley",
  "Mountain West",
  "Northeast",
  "Ohio Valley",
  "Pac-12",
  "Patriot League",
  "SEC",
  "Southern",
  "Southland",
  "Summit League",
  "Sun Belt",
  "WAC",
  "West Coast",
];

const REGIONS = ["Northeast", "Midwest", "South", "West"];

const ASSOCIATIONS = [
  { value: "NCAA", label: "NCAA" },
  { value: "NAIA", label: "NAIA" },
];

const OWNERSHIP_TYPES = ["Public", "Private nonprofit", "Private for-profit"];

const LOCALE_TYPES = ["City", "Suburb", "Town", "Rural"];

/* ─── School Detail Panel ─── */

function SchoolDetail({
  school,
  userGenderLabel,
  collegeData,
  collegeLoading,
  fit,
  onAssessFit,
  playerScoringDifferential,
  playerClassRank,
  isInPipeline,
  pipelineLoading,
  onTogglePipeline,
  notes,
  onSaveNotes,
}: {
  school: SchoolResult;
  userGenderLabel: string;
  collegeData: CollegeDetail | null;
  collegeLoading: boolean;
  fit?: GolfFitAssessment | null;
  onAssessFit: () => void;
  playerScoringDifferential: number | null;
  playerClassRank: number | null;
  isInPipeline: boolean;
  pipelineLoading: boolean;
  onTogglePipeline: () => void;
  notes: string;
  onSaveNotes: (notes: string) => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const brandedSchool = {
    ...school,
    city: collegeData?.city || school.city,
    logoUrl: collegeData?.logo_url || school.logoUrl,
    brandColorPrimary: collegeData?.brand_color_primary || school.brandColorPrimary,
    brandColorSecondary: collegeData?.brand_color_secondary || school.brandColorSecondary,
  };

  return (
    <div className="overflow-hidden">
      <SchoolBrandHeader school={brandedSchool} fit={fit} variant="detail" />
      <div className="border-b border-border bg-white p-4 sm:px-6">
        {isInPipeline ? (
          <>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-11 flex-1 items-center gap-2 rounded-lg border border-lime-border bg-lime-soft px-3.5 text-xs font-extrabold text-primary-text">
                <Check className="h-4 w-4" /> In My Schools
              </span>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/pipeline")}
                className="h-11 gap-1.5 text-xs"
              >
                Open My Schools <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
            <button
              type="button"
              onClick={onTogglePipeline}
              disabled={pipelineLoading}
              aria-pressed={isInPipeline}
              className="mt-2 text-[11px] font-semibold text-subtle hover:text-warn hover:underline disabled:cursor-wait disabled:opacity-60"
            >
              {pipelineLoading ? "Updating…" : "Remove from My Schools"}
            </button>
          </>
        ) : (
          <Button
            type="button"
            onClick={onTogglePipeline}
            disabled={pipelineLoading}
            aria-pressed={isInPipeline}
            className="h-11 w-full gap-2 text-xs"
          >
            <Plus className="h-4 w-4" />
            {pipelineLoading ? "Updating…" : "Add to My Schools"}
          </Button>
        )}
      </div>
      <div className="space-y-4 bg-surface p-4 sm:p-6">
        <SchoolFitAssessmentPanel
          fit={fit}
          onRefresh={onAssessFit}
          schoolName={school.schoolName}
          onAskCoach={() =>
            navigate("/chat", {
              state: {
                autoPrompt: `I want to learn more about ${school.schoolName} and its college golf program. Using my profile, the school's information, and my existing fit assessment, give me a recruiting-focused overview of its competitive level, recruiting patterns, roster and coaching considerations, how my game may fit, and the most important questions I should research or ask the coach. Clearly separate verified information from anything I still need to confirm.`,
              },
            })
          }
          playerScoringDifferential={playerScoringDifferential}
          programAverageScoringDifferential={
            school.commit_score_stats?.avg_score_differential ?? null
          }
          playerClassRank={playerClassRank}
          programAverageClassRank={school.commit_score_stats?.avg_class_rank ?? null}
        />

        <SchoolProgramDetailsPanel
          school={school}
          userGenderLabel={userGenderLabel}
          collegeData={collegeData}
          collegeLoading={collegeLoading}
        />

        <SchoolNotesPanel schoolId={school.schoolId} notes={notes} onSave={onSaveNotes} />
      </div>
    </div>
  );
}

/**
 * The selected school slides out over the results, the same panel shape as
 * My Schools (SchoolPortfolioDetail): a right-hand sheet on desktop, a
 * bottom drawer on phones. The results keep the full width (ZON-281).
 */
function SchoolDetailPanel({
  school,
  onClose,
  children,
}: {
  school: SchoolResult | null;
  onClose: () => void;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  const title = school?.schoolName || "School details";
  const description = "Golf fit, program details and notes";
  const onOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  if (isMobile) {
    return (
      <Drawer open={!!school} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[92vh] overflow-hidden rounded-t-[24px] border-border bg-surface">
          <DrawerHeader className="sr-only">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="relative min-h-0 overflow-y-auto">{school && children}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={!!school} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[500px] flex-col overflow-hidden border-l-0 bg-surface p-0 sm:max-w-[500px]"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <div className="relative min-h-0 flex-1 overflow-y-auto">{school && children}</div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Schools Content ─── */

function SchoolsContent() {
  const navigate = useNavigate();
  const { currentUser, playerSummary } = useEndUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const initialSchoolId = searchParams.get("id") || "";

  // Determine default gender from user profile
  const defaultGender = useMemo(() => {
    const g = currentUser?.gender;
    if (g === "male" || g === "Male") return "Men";
    if (g === "female" || g === "Female") return "Women";
    return "Men";
  }, [currentUser]);

  useEffect(() => {
    trackSchoolsViewed();
  }, []);

  // Search / filter state
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQuery);
  const [gender, setGender] = useState(defaultGender);
  const [division, setDivision] = useState("");
  const [state, setState] = useState("");
  const [conference, setConference] = useState("");
  const [region, setRegion] = useState("");
  const [association, setAssociation] = useState("");
  const [minRankInput, setMinRankInput] = useState("");
  const [maxRankInput, setMaxRankInput] = useState("");
  const [minDifferentialInput, setMinDifferentialInput] = useState("");
  const [maxDifferentialInput, setMaxDifferentialInput] = useState("");
  const [maxAcceptanceRateInput, setMaxAcceptanceRateInput] = useState("");
  const [maxNetPriceInput, setMaxNetPriceInput] = useState("");
  const [majorInput, setMajorInput] = useState("");
  const [allMajors, setAllMajors] = useState<string[]>([]);
  const [minGradRateInput, setMinGradRateInput] = useState("");
  const [hasCoachEmail, setHasCoachEmail] = useState(false);
  const [ownership, setOwnership] = useState("");
  const [localeType, setLocaleType] = useState("");
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  // Debounced numeric inputs (500ms)
  const [debouncedMinRank, setDebouncedMinRank] = useState("");
  const [debouncedMaxRank, setDebouncedMaxRank] = useState("");
  const [debouncedMinDifferential, setDebouncedMinDifferential] = useState("");
  const [debouncedMaxDifferential, setDebouncedMaxDifferential] = useState("");
  const [debouncedMaxAcceptanceRate, setDebouncedMaxAcceptanceRate] = useState("");
  const [debouncedMaxNetPrice, setDebouncedMaxNetPrice] = useState("");
  const [debouncedMajor, setDebouncedMajor] = useState("");
  const [debouncedMinGradRate, setDebouncedMinGradRate] = useState("");
  const [page, setPage] = useState(0);

  // Results state
  const [results, setResults] = useState<SchoolResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState<SchoolResult | null>(null);

  // Multi-school deep link: /schools?ids=<teamId1>,<teamId2>,... (digest email CTA)
  const {
    idsMode,
    schools: idsSchools,
    loading: idsLoading,
    exitIdsMode,
  } = useSchoolsFromIdsParam();

  // What the results pane shows: the ids-derived list while in ids mode,
  // otherwise normal search results.
  const displayResults = idsMode ? idsSchools : results;
  const displayTotal = idsMode ? idsSchools.length : total;
  const displayLoading = idsMode ? idsLoading : loading;

  // Any search/filter interaction exits ids mode and resumes normal search.
  const idsModeInteractionArmed = useRef(false);

  // College enrichment data (secondary fetch)
  const [collegeData, setCollegeData] = useState<CollegeDetail | null>(null);
  const [collegeLoading, setCollegeLoading] = useState(false);

  useEffect(() => {
    const collegeId = selectedSchool?.college_id;
    if (!collegeId) {
      setCollegeData(null);
      return;
    }
    let cancelled = false;
    setCollegeLoading(true);
    setCollegeData(null);
    fetchCollege(collegeId)
      .then(data => {
        if (!cancelled) setCollegeData(data);
      })
      .catch(() => {}) // silently fail — enrichment is optional
      .finally(() => {
        if (!cancelled) setCollegeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSchool?.college_id]);

  // Direct link: fetch school by ID from ?id= param
  useEffect(() => {
    if (!initialSchoolId) return;
    let cancelled = false;
    async function loadDirect() {
      try {
        const school = await fetchSchoolById(initialSchoolId);
        if (!cancelled) {
          setSelectedSchool(school);
          setSearchInput(school.schoolName);
          setDebouncedSearch(school.schoolName);
          // Clean up URL param
          setSearchParams({}, { replace: true });
        }
      } catch (err) {
        console.error("Failed to load school by ID:", err);
      }
    }
    loadDirect();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pipeline state
  const [pipelineTeamIds, setPipelineTeamIds] = useState<Set<string>>(new Set());
  const [pipelineMap, setPipelineMap] = useState<Map<string, { _id: string }>>(new Map());
  const pipelineActionsInFlight = useRef(new Set<string>());
  const [pipelineActionTeamIds, setPipelineActionTeamIds] = useState<Set<string>>(new Set());

  // Standalone notes state (decoupled from pipeline)
  const [notesMap, setNotesMap] = useState<Map<string, string>>(new Map()); // teamId -> notes text

  // Canonical AI Coach assessments shared by School Search and My Schools.
  const [schoolFitMap, setSchoolFitMap] = useState<Map<string, GolfFitAssessment>>(new Map());
  const [assessingTeamIds, setAssessingTeamIds] = useState<Set<string>>(new Set());
  const assessmentsInFlight = useRef(new Set<string>());

  const linkedJgsProfile = useMemo(() => {
    try {
      return sessionStorage.getItem("recruit_ai_coach_active_profile") || undefined;
    } catch {
      return undefined;
    }
  }, []);

  const playerClassRank = useMemo(() => {
    const value = Number(playerSummary.classRank);
    return playerSummary.classRank && Number.isFinite(value) ? value : null;
  }, [playerSummary.classRank]);

  const playerScoringDifferential = useMemo(() => {
    if (!playerSummary.scoringDifferential || playerSummary.scoringDifferential === "N/A") {
      return null;
    }
    const value = Number(playerSummary.scoringDifferential);
    return Number.isFinite(value) ? value : null;
  }, [playerSummary.scoringDifferential]);

  // Load majors list for typeahead
  const majorsLoaded = useRef(false);
  useEffect(() => {
    if (majorsLoaded.current) return;
    majorsLoaded.current = true;
    fetchMajorsList()
      .then(setAllMajors)
      .catch(() => {});
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounce numeric inputs (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMinRank(minRankInput);
      setDebouncedMaxRank(maxRankInput);
      setDebouncedMinDifferential(minDifferentialInput);
      setDebouncedMaxDifferential(maxDifferentialInput);
      setDebouncedMaxAcceptanceRate(maxAcceptanceRateInput);
      setDebouncedMaxNetPrice(maxNetPriceInput);
      setDebouncedMajor(majorInput);
      setDebouncedMinGradRate(minGradRateInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [
    minRankInput,
    maxRankInput,
    minDifferentialInput,
    maxDifferentialInput,
    maxAcceptanceRateInput,
    maxNetPriceInput,
    majorInput,
    minGradRateInput,
  ]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [
    gender,
    division,
    state,
    conference,
    region,
    association,
    hasCoachEmail,
    ownership,
    localeType,
  ]);

  // Leave ids mode as soon as the user changes any search/filter input
  // (first run on mount is skipped — deps only change on real interaction).
  useEffect(() => {
    if (!idsModeInteractionArmed.current) {
      idsModeInteractionArmed.current = true;
      return;
    }
    exitIdsMode();
  }, [
    debouncedSearch,
    gender,
    division,
    state,
    conference,
    region,
    association,
    debouncedMinRank,
    debouncedMaxRank,
    debouncedMinDifferential,
    debouncedMaxDifferential,
    debouncedMaxAcceptanceRate,
    debouncedMaxNetPrice,
    debouncedMajor,
    debouncedMinGradRate,
    hasCoachEmail,
    ownership,
    localeType,
    page,
    exitIdsMode,
  ]);

  // Fetch schools — use groupByCollege mode so all colleges appear
  useEffect(() => {
    if (idsMode) return; // ids deep link supplies the results directly
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const resp = await fetchSchools({
          q: debouncedSearch || undefined,
          // Don't pass gender as a hard filter — pass it as preferGender instead
          division: division || undefined,
          state: state || undefined,
          conference: conference || undefined,
          region: region || undefined,
          association: association || undefined,
          min_rank: debouncedMinRank ? Number(debouncedMinRank) : undefined,
          max_rank: debouncedMaxRank ? Number(debouncedMaxRank) : undefined,
          min_differential: debouncedMinDifferential ? Number(debouncedMinDifferential) : undefined,
          max_differential: debouncedMaxDifferential ? Number(debouncedMaxDifferential) : undefined,
          max_acceptance_rate: debouncedMaxAcceptanceRate
            ? Number(debouncedMaxAcceptanceRate)
            : undefined,
          max_net_price: debouncedMaxNetPrice ? Number(debouncedMaxNetPrice) : undefined,
          major: debouncedMajor || undefined,
          min_grad_rate: debouncedMinGradRate ? Number(debouncedMinGradRate) : undefined,
          has_coach_email: hasCoachEmail || undefined,
          ownership: ownership || undefined,
          locale_type: localeType || undefined,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          groupByCollege: true,
          preferGender: gender !== "All" ? gender : undefined,
          include_club_golf: currentUser?.showClubGolf ? true : undefined,
        });
        if (!cancelled) {
          setResults(resp.results);
          setTotal(resp.total);
          if (debouncedSearch) {
            trackSchoolSearched({ query: debouncedSearch, resultCount: resp.total });
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to search schools:", err);
          toast.error("Failed to search schools");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [
    idsMode,
    debouncedSearch,
    gender,
    division,
    state,
    conference,
    region,
    association,
    debouncedMinRank,
    debouncedMaxRank,
    debouncedMinDifferential,
    debouncedMaxDifferential,
    debouncedMaxAcceptanceRate,
    debouncedMaxNetPrice,
    debouncedMajor,
    debouncedMinGradRate,
    hasCoachEmail,
    ownership,
    localeType,
    page,
    currentUser,
  ]);

  // Load pipeline on mount to know which schools are already added
  const loadPipelineMembership = useCallback(async () => {
    try {
      const resp = await fetchPipeline({ linkedJgsProfile });
      const ids = new Set<string>();
      const map = new Map<string, { _id: string }>();
      for (const entry of resp.pipeline) {
        if (entry.school.teamId) {
          ids.add(entry.school.teamId);
          map.set(entry.school.teamId, { _id: entry._id });
        }
      }
      setPipelineTeamIds(ids);
      setPipelineMap(map);
    } catch (err) {
      console.error("Failed to load pipeline:", err);
    }
  }, [linkedJgsProfile]);

  useEffect(() => {
    void loadPipelineMembership();
  }, [loadPipelineMembership]);

  // Load school notes on mount (parallel, decoupled from pipeline)
  useEffect(() => {
    async function loadNotes() {
      try {
        const resp = await fetchSchoolNotes({ linkedJgsProfile });
        const map = new Map<string, string>();
        for (const note of resp.notes) {
          map.set(note.teamId, note.notes);
        }
        setNotesMap(map);
      } catch (err) {
        console.error("Failed to load school notes:", err);
      }
    }
    loadNotes();
  }, [linkedJgsProfile]);

  const fitTeamIdsKey = useMemo(() => {
    const ids = displayResults.map(school => school.schoolId).filter(Boolean);
    if (selectedSchool?.schoolId) ids.push(selectedSchool.schoolId);
    return [...new Set(ids)].sort().join(",");
  }, [displayResults, selectedSchool?.schoolId]);

  // Load stored assessments without automatically invoking the AI Coach for every result.
  useEffect(() => {
    if (!fitTeamIdsKey) return;
    let cancelled = false;
    fetchSchoolGolfFits(fitTeamIdsKey.split(","), linkedJgsProfile)
      .then(assessments => {
        if (cancelled) return;
        setSchoolFitMap(current => {
          const next = new Map(current);
          for (const assessment of assessments) {
            if (assessment.teamId) next.set(assessment.teamId, assessment);
          }
          return next;
        });
      })
      .catch(error => console.error("Failed to load golf-fit assessments:", error));
    return () => {
      cancelled = true;
    };
  }, [fitTeamIdsKey, linkedJgsProfile]);

  const assessGolfFit = useCallback(
    async (school: SchoolResult) => {
      const teamId = school.schoolId;
      if (!teamId || assessmentsInFlight.current.has(teamId)) return;

      const previous = schoolFitMap.get(teamId);
      assessmentsInFlight.current.add(teamId);
      setAssessingTeamIds(new Set(assessmentsInFlight.current));
      setSchoolFitMap(current =>
        new Map(current).set(teamId, {
          ...previous,
          teamId,
          schoolName: school.schoolName,
          status: "updating",
          score: previous?.score ?? null,
          tier: previous?.tier ?? null,
          summary: previous?.summary ?? null,
          explanation: previous?.explanation ?? null,
          assessedAt: previous?.assessedAt ?? null,
        })
      );

      try {
        const assessment = await refreshSchoolGolfFit(
          teamId,
          {
            schoolName: school.schoolName,
            division: school.division,
            gender: school.gender,
          },
          linkedJgsProfile
        );
        setSchoolFitMap(current => new Map(current).set(teamId, assessment));
        toast.success(`Golf fit assessed for ${school.schoolName}`);
      } catch (error) {
        console.error("Failed to assess golf fit:", error);
        setSchoolFitMap(current =>
          new Map(current).set(teamId, {
            ...previous,
            teamId,
            schoolName: school.schoolName,
            status: "failed",
            score: previous?.score ?? null,
            tier: previous?.tier ?? null,
            summary: previous?.summary ?? null,
            explanation: previous?.explanation ?? null,
            assessedAt: previous?.assessedAt ?? null,
          })
        );
        toast.error("The AI Coach could not assess this school");
      } finally {
        assessmentsInFlight.current.delete(teamId);
        setAssessingTeamIds(new Set(assessmentsInFlight.current));
      }
    },
    [linkedJgsProfile, schoolFitMap]
  );

  const isInPipeline = useCallback(
    (teamId: string | null | undefined) => !!teamId && pipelineTeamIds.has(teamId),
    [pipelineTeamIds]
  );

  const togglePipeline = useCallback(
    async (school: SchoolResult) => {
      if (!school.schoolId) return;
      if (pipelineActionsInFlight.current.has(school.schoolId)) return;
      pipelineActionsInFlight.current.add(school.schoolId);
      setPipelineActionTeamIds(new Set(pipelineActionsInFlight.current));
      try {
        if (isInPipeline(school.schoolId)) {
          // Remove from pipeline
          const pipelineEntry = pipelineMap.get(school.schoolId);
          if (pipelineEntry) {
            await removePipelineEntry(pipelineEntry._id, linkedJgsProfile);
            setPipelineTeamIds(prev => {
              const next = new Set(prev);
              next.delete(school.schoolId);
              return next;
            });
            setPipelineMap(prev => {
              const next = new Map(prev);
              next.delete(school.schoolId);
              return next;
            });
            trackSchoolPipelineToggled({
              schoolName: school.schoolName,
              schoolId: school.schoolId,
              added: false,
            });
            toast.success("Removed from My Schools");
          }
        } else {
          // Add to pipeline
          const entry = await addToPipeline(
            {
              schoolName: school.schoolName,
              teamId: school.schoolId,
              division: school.division,
              conference: school.conference,
              state: school.state,
              headCoach: school.headCoach,
              headCoachEmail: school.headCoachEmail,
              gender: school.gender,
            },
            linkedJgsProfile
          );
          setPipelineTeamIds(prev => new Set(prev).add(school.schoolId));
          setPipelineMap(prev => new Map(prev).set(school.schoolId, { _id: entry._id }));
          trackSchoolPipelineToggled({
            schoolName: school.schoolName,
            schoolId: school.schoolId,
            added: true,
          });
          toast.success("Added to My Schools");
        }
      } catch (err: any) {
        // 409 means already exists
        if (err?.message?.includes("409")) {
          toast.info("School is already in My Schools");
          await loadPipelineMembership();
        } else {
          toast.error("Failed to update My Schools");
        }
      } finally {
        pipelineActionsInFlight.current.delete(school.schoolId);
        setPipelineActionTeamIds(new Set(pipelineActionsInFlight.current));
      }
    },
    [isInPipeline, pipelineMap, linkedJgsProfile, loadPipelineMembership]
  );

  const handleUpdateNotes = useCallback(
    async (teamId: string, notes: string, schoolName: string) => {
      const prev = notesMap.get(teamId) ?? null;

      // Optimistic update
      setNotesMap(map => {
        const next = new Map(map);
        if (notes.trim()) {
          next.set(teamId, notes);
        } else {
          next.delete(teamId);
        }
        return next;
      });

      try {
        await upsertSchoolNote(teamId, notes, schoolName, linkedJgsProfile);
      } catch (err) {
        toast.error("Failed to save note");
        // Revert
        setNotesMap(map => {
          const next = new Map(map);
          if (prev) {
            next.set(teamId, prev);
          } else {
            next.delete(teamId);
          }
          return next;
        });
      }
    },
    [notesMap, linkedJgsProfile]
  );

  const clearAllFilters = useCallback(() => {
    setSearchInput("");
    setDebouncedSearch("");
    setGender(defaultGender);
    setDivision("");
    setState("");
    setConference("");
    setRegion("");
    setAssociation("");
    setMinRankInput("");
    setMaxRankInput("");
    setMinDifferentialInput("");
    setMaxDifferentialInput("");
    setMaxAcceptanceRateInput("");
    setMaxNetPriceInput("");
    setMajorInput("");
    setMinGradRateInput("");
    setDebouncedMinRank("");
    setDebouncedMaxRank("");
    setDebouncedMinDifferential("");
    setDebouncedMaxDifferential("");
    setDebouncedMaxAcceptanceRate("");
    setDebouncedMaxNetPrice("");
    setDebouncedMajor("");
    setDebouncedMinGradRate("");
    setHasCoachEmail(false);
    setOwnership("");
    setLocaleType("");
    setPage(0);
  }, [defaultGender]);

  // Chips for "More filters" section values
  const moreFilterChips = useMemo(() => {
    const chips: { label: string; onClear: () => void }[] = [];
    if (state) chips.push({ label: state, onClear: () => setState("") });
    if (region) chips.push({ label: region, onClear: () => setRegion("") });
    if (conference) chips.push({ label: conference, onClear: () => setConference("") });
    if (association) chips.push({ label: association, onClear: () => setAssociation("") });
    if (minRankInput || maxRankInput) {
      const label = `Rank: ${minRankInput || "1"}\u2013${maxRankInput || "\u221E"}`;
      chips.push({
        label,
        onClear: () => {
          setMinRankInput("");
          setMaxRankInput("");
        },
      });
    }
    if (minDifferentialInput || maxDifferentialInput) {
      const label = `Differential: ${minDifferentialInput || "\u2212\u221E"}\u2013${maxDifferentialInput || "\u221E"}`;
      chips.push({
        label,
        onClear: () => {
          setMinDifferentialInput("");
          setMaxDifferentialInput("");
        },
      });
    }
    if (hasCoachEmail)
      chips.push({ label: "Has coach email", onClear: () => setHasCoachEmail(false) });
    if (maxAcceptanceRateInput) {
      chips.push({
        label: `Acceptance \u2264 ${maxAcceptanceRateInput}%`,
        onClear: () => setMaxAcceptanceRateInput(""),
      });
    }
    if (maxNetPriceInput) {
      chips.push({
        label: `Net Price \u2264 $${Number(maxNetPriceInput).toLocaleString()}`,
        onClear: () => setMaxNetPriceInput(""),
      });
    }
    if (majorInput) {
      chips.push({ label: `Major: ${majorInput}`, onClear: () => setMajorInput("") });
    }
    if (minGradRateInput) {
      chips.push({
        label: `Grad Rate \u2265 ${minGradRateInput}%`,
        onClear: () => setMinGradRateInput(""),
      });
    }
    if (ownership) chips.push({ label: ownership, onClear: () => setOwnership("") });
    if (localeType) chips.push({ label: localeType, onClear: () => setLocaleType("") });
    return chips;
  }, [
    state,
    region,
    conference,
    association,
    minRankInput,
    maxRankInput,
    minDifferentialInput,
    maxDifferentialInput,
    maxAcceptanceRateInput,
    maxNetPriceInput,
    majorInput,
    minGradRateInput,
    hasCoachEmail,
    ownership,
    localeType,
  ]);

  const hasMoreFilters = moreFilterChips.length > 0;

  const totalPages = Math.ceil(displayTotal / PAGE_SIZE);
  const showingFrom = displayTotal === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, displayTotal);

  const hasActiveFilters = debouncedSearch || division || hasMoreFilters;

  // Ref for scrolling results list to top on page change
  const resultsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    resultsRef.current?.scrollTo({ top: 0 });
  }, [
    page,
    debouncedSearch,
    division,
    state,
    conference,
    region,
    association,
    debouncedMinRank,
    debouncedMaxRank,
    hasCoachEmail,
    ownership,
    localeType,
  ]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Content: full-width results; the selected school slides out over them */}
      <div className="flex-1 min-h-0 flex">
        {/* Search + Results */}
        <div className="flex flex-1 flex-col min-h-0">
          {/* Header + Filters */}
          <div className="shrink-0 px-4 pt-5 pb-3 space-y-3">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h1 className={PAGE_TITLE_CLASS}>School Search</h1>
                <p className={PAGE_SUBTITLE_CLASS}>
                  Assess your golf fit, then save programs to your recruiting portfolio.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate("/pipeline")}
                className="shrink-0 gap-1.5 rounded-xl"
              >
                My Schools <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Search + filters card */}
            <div className="rounded-lg border border-border bg-card p-3 space-y-2">
              {/* Search input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search schools..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pl-9 h-9"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Primary filter row */}
              <div className="grid grid-cols-2 gap-2">
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All</SelectItem>
                    {defaultGender !== "Women" && (
                      <SelectItem value="Men">Has Men&apos;s Team</SelectItem>
                    )}
                    {defaultGender !== "Men" && (
                      <SelectItem value="Women">Has Women&apos;s Team</SelectItem>
                    )}
                  </SelectContent>
                </Select>

                <Select
                  value={division || "all"}
                  onValueChange={v => setDivision(v === "all" ? "" : v)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Division" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Divisions</SelectItem>
                    {DIVISIONS.map(d => (
                      <SelectItem key={d.value} value={d.value}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* More filters — shared body rendered in Sheet (mobile) or Collapsible (desktop) */}
              {(() => {
                const filterBody = (
                  <div className="space-y-3">
                    {/* Location & Conference */}
                    <div className="rounded-lg border border-border/60 bg-card p-3 space-y-2.5">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Location & Conference
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select
                          value={state || "all"}
                          onValueChange={v => setState(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9 text-xs bg-background">
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All States</SelectItem>
                            {US_STATES.map(s => (
                              <SelectItem key={s} value={s}>
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={region || "all"}
                          onValueChange={v => setRegion(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9 text-xs bg-background">
                            <SelectValue placeholder="Region" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Regions</SelectItem>
                            {REGIONS.map(r => (
                              <SelectItem key={r} value={r}>
                                {r}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={conference || "all"}
                          onValueChange={v => setConference(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9 text-xs bg-background">
                            <SelectValue placeholder="Conference" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Conferences</SelectItem>
                            {CONFERENCES.map(c => (
                              <SelectItem key={c} value={c}>
                                {c}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={association || "all"}
                          onValueChange={v => setAssociation(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9 text-xs bg-background">
                            <SelectValue placeholder="Association" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Associations</SelectItem>
                            {ASSOCIATIONS.map(a => (
                              <SelectItem key={a.value} value={a.value}>
                                {a.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={localeType || "all"}
                          onValueChange={v => setLocaleType(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9 text-xs bg-background">
                            <SelectValue placeholder="Setting" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Settings</SelectItem>
                            {LOCALE_TYPES.map(l => (
                              <SelectItem key={l} value={l}>
                                {l}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Golf Program */}
                    <div className="rounded-lg border border-border/60 bg-card p-3 space-y-3">
                      <div className="flex items-center gap-1.5">
                        <Trophy className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Golf Program
                        </h3>
                      </div>
                      <div className="space-y-2.5">
                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-muted-foreground">
                            National rank
                          </label>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              placeholder="Min"
                              value={minRankInput}
                              onChange={e => setMinRankInput(e.target.value)}
                              className="h-9 text-sm bg-background"
                              min={1}
                            />
                            <span className="text-xs text-muted-foreground shrink-0">&ndash;</span>
                            <Input
                              type="number"
                              placeholder="Max"
                              value={maxRankInput}
                              onChange={e => setMaxRankInput(e.target.value)}
                              className="h-9 text-sm bg-background"
                              min={1}
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-muted-foreground">
                            Avg Score Differential
                          </label>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              placeholder="Min"
                              value={minDifferentialInput}
                              onChange={e => setMinDifferentialInput(e.target.value)}
                              className="h-9 text-sm bg-background"
                              step="0.1"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">&ndash;</span>
                            <Input
                              type="number"
                              placeholder="Max"
                              value={maxDifferentialInput}
                              onChange={e => setMaxDifferentialInput(e.target.value)}
                              className="h-9 text-sm bg-background"
                              step="0.1"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-md bg-background/60 px-3 py-2">
                        <label
                          htmlFor="coach-email-switch"
                          className="text-xs text-muted-foreground"
                        >
                          Has coach email
                        </label>
                        <Switch
                          id="coach-email-switch"
                          checked={hasCoachEmail}
                          onCheckedChange={setHasCoachEmail}
                        />
                      </div>
                    </div>

                    {/* Admissions */}
                    <div className="rounded-lg border border-border/60 bg-card p-3 space-y-2.5">
                      <div className="flex items-center gap-1.5">
                        <GraduationCap className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Admissions
                        </h3>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">
                          Max Acceptance Rate
                        </label>
                        <div className="relative">
                          <Input
                            type="number"
                            placeholder="e.g. 50"
                            value={maxAcceptanceRateInput}
                            onChange={e => setMaxAcceptanceRateInput(e.target.value)}
                            className="h-9 text-sm bg-background pr-7"
                            min={0}
                            max={100}
                            step="1"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            %
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Cost */}
                    <div className="rounded-lg border border-border/60 bg-card p-3 space-y-2.5">
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Cost
                        </h3>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">
                          Max Net Price
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            $
                          </span>
                          <Input
                            type="number"
                            placeholder="e.g. 25000"
                            value={maxNetPriceInput}
                            onChange={e => setMaxNetPriceInput(e.target.value)}
                            className="h-9 text-sm bg-background pl-7"
                            min={0}
                            step="1000"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Academics */}
                    <div className="rounded-lg border border-border/60 bg-card p-3 space-y-2.5">
                      <div className="flex items-center gap-1.5">
                        <BookOpen className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Academics
                        </h3>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">Major</label>
                        <MajorCombobox
                          value={majorInput}
                          onChange={setMajorInput}
                          majors={allMajors}
                          className="h-9 rounded-md"
                        />
                      </div>
                    </div>

                    {/* Outcomes */}
                    <div className="rounded-lg border border-border/60 bg-card p-3 space-y-2.5">
                      <div className="flex items-center gap-1.5">
                        <TrendingUp className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Outcomes
                        </h3>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">
                          Min Graduation Rate
                        </label>
                        <div className="relative">
                          <Input
                            type="number"
                            placeholder="e.g. 60"
                            value={minGradRateInput}
                            onChange={e => setMinGradRateInput(e.target.value)}
                            className="h-9 text-sm bg-background pr-7"
                            min={0}
                            max={100}
                            step="1"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            %
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* School Type */}
                    <div className="rounded-lg border border-border/60 bg-card p-3 space-y-2.5">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                          School Type
                        </h3>
                      </div>
                      <Select
                        value={ownership || "all"}
                        onValueChange={v => setOwnership(v === "all" ? "" : v)}
                      >
                        <SelectTrigger className="h-9 text-xs bg-background">
                          <SelectValue placeholder="Ownership" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {OWNERSHIP_TYPES.map(o => (
                            <SelectItem key={o} value={o}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );

                return (
                  <>
                    {/* Mobile: Sheet overlay */}
                    <div className="md:hidden">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs gap-1.5 px-2 text-muted-foreground hover:text-foreground w-full justify-start"
                        onClick={() => setMoreFiltersOpen(true)}
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        More filters
                        {hasMoreFilters && (
                          <span className="ml-auto rounded-full bg-primary/15 text-primary-text text-[11px] font-semibold px-1.5 py-0.5 leading-none">
                            {moreFilterChips.length}
                          </span>
                        )}
                      </Button>
                      <Sheet open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
                        <SheetContent side="left" className="w-[85%] max-w-sm overflow-y-auto">
                          <SheetHeader>
                            <SheetTitle>More filters</SheetTitle>
                          </SheetHeader>
                          <div className="pt-4">{filterBody}</div>
                        </SheetContent>
                      </Sheet>
                    </div>

                    {/* Desktop: Inline collapsible */}
                    <div className="hidden md:block">
                      <Collapsible open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs gap-1.5 px-2 text-muted-foreground hover:text-foreground w-full justify-start"
                          >
                            <SlidersHorizontal className="h-3.5 w-3.5" />
                            More filters
                            <ChevronDown
                              className={`h-3.5 w-3.5 ml-auto transition-transform ${moreFiltersOpen ? "rotate-180" : ""}`}
                            />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="pt-2">{filterBody}</div>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Active filter chips */}
            {hasMoreFilters && (
              <div className="flex flex-wrap items-center gap-1.5">
                {moreFilterChips.map(({ label, onClear }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium pr-1.5"
                  >
                    {label}
                    <button type="button" onClick={onClear} className="hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Results list */}
          <div ref={resultsRef} className="relative flex-1 min-h-0 overflow-y-auto px-4 pb-2">
            {displayLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-pulse text-muted-foreground text-sm">Searching...</div>
              </div>
            ) : displayResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Search className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">
                  {hasActiveFilters
                    ? "No schools match your filters"
                    : "Search for schools to get started"}
                </p>
              </div>
            ) : (
              <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                {displayResults.map(school => (
                  <SchoolSearchCard
                    key={school.schoolId}
                    school={school}
                    fit={schoolFitMap.get(school.schoolId)}
                    selected={selectedSchool?.schoolId === school.schoolId}
                    playerScoringDifferential={playerScoringDifferential}
                    playerClassRank={playerClassRank}
                    isInMySchools={isInPipeline(school.schoolId)}
                    membershipLoading={pipelineActionTeamIds.has(school.schoolId)}
                    onOpen={() => setSelectedSchool(school)}
                    onToggleMySchools={() => void togglePipeline(school)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {/* Only when there is more than one page; on phones it would otherwise stack on the tab bar */}
          {displayTotal > 0 && totalPages > 1 && (
            <div className="shrink-0 border-t border-border px-4 py-1.5 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {showingFrom}&ndash;{showingTo} of {displayTotal}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  aria-label="Previous page"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums px-1">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  aria-label="Next page"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <SchoolDetailPanel school={selectedSchool} onClose={() => setSelectedSchool(null)}>
          {selectedSchool && (
            <SchoolDetail
              school={selectedSchool}
              userGenderLabel={gender}
              collegeData={collegeData}
              collegeLoading={collegeLoading}
              fit={schoolFitMap.get(selectedSchool.schoolId)}
              onAssessFit={() => void assessGolfFit(selectedSchool)}
              isInPipeline={isInPipeline(selectedSchool.schoolId)}
              pipelineLoading={pipelineActionTeamIds.has(selectedSchool.schoolId)}
              onTogglePipeline={() => void togglePipeline(selectedSchool)}
              playerScoringDifferential={playerScoringDifferential}
              playerClassRank={playerClassRank}
              notes={notesMap.get(selectedSchool.schoolId) ?? ""}
              onSaveNotes={notes =>
                handleUpdateNotes(selectedSchool.schoolId, notes, selectedSchool.schoolName)
              }
            />
          )}
        </SchoolDetailPanel>
      </div>
    </div>
  );
}

/* ─── Page Wrapper ─── */

const Schools = () => {
  const navigate = useNavigate();
  const { isSidebarOpen, closeSidebar, chatHistory, playerSummary } = useEndUser();
  const { isLinked } = playerSummary;

  return (
    <TwoPaneLayout
      isSidebarOpen={isSidebarOpen}
      onCloseSidebar={closeSidebar}
      sidebar={
        <Sidebar
          chatHistory={chatHistory}
          onNewChat={() => navigate("/chat")}
          onClose={closeSidebar}
          onSelectChat={id => navigate(`/chat?thread=${id}`)}
          isLinked={isLinked}
        />
      }
      main={<SchoolsContent />}
    />
  );
};

export default Schools;
